import React, { useState, useEffect } from "react";
import { X, Edit3, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { errMsg } from "../lib/api";
import SearchableSelect from "./ui/SearchableSelect";

// Owner rule (20 Oct 2026): these four are the only product types.
const CATEGORIES = ["Retail", "Technical", "Disposable", "Electrical"];
const QTY_UNITS = ["pcs", "grm", "ml"];
const STOCK_UNITS = ["bottle", "packet", "box", "pcs"];
const normQtyUnit = (u) => {
  const v = (u || "").toLowerCase();
  if (["piece", "pcs", "pc"].includes(v)) return "pcs";
  if (["gram", "gm", "grm", "gms"].includes(v)) return "grm";
  if (["ml", "millilitre", "milliliter"].includes(v)) return "ml";
  if (["ltr", "litre", "liter", "l"].includes(v)) return "ltr";
  if (["kg", "kgs"].includes(v)) return "kg";
  return v || "pcs";
};
const normStockUnit = (u) => {
  const v = (u || "").toLowerCase();
  if (["bottle", "bottles"].includes(v)) return "bottle";
  if (["packet", "packets", "pkt"].includes(v)) return "packet";
  if (["box", "boxes"].includes(v)) return "box";
  if (["piece", "pieces", "pcs", "pc"].includes(v)) return "pcs";
  return v || "";
};

export default function EditProductModal({ product, products = [], onClose, onSuccess }) {
  const [name, setName] = useState(product?.name || "");
  const [category, setCategory] = useState(product?.category || "Retail");
  const [ledger, setLedger] = useState(product?.ledger || "retail");
  const [brand, setBrand] = useState(product?.brand || "");
  const [vendorName, setVendorName] = useState(product?.vendor_name || "");
  const [unit, setUnit] = useState(normQtyUnit(product?.unit));
  const [stockUnit, setStockUnit] = useState(normStockUnit(product?.stock_unit || product?.unit));
  const [qty, setQty] = useState(product?.qty || 0);
  const [unitPrice, setUnitPrice] = useState(product?.unit_price || 0);
  // Owner rule (20 Oct 2026): one main stock only. The field reads the same
  // number the Inventory list and the Stock Audit count, and saving writes it
  // into the single stock bucket (no sealed / floor split).
  const [storeQty, setStoreQty] = useState(
    product?.opening_stock ?? product?.current_stock ?? (Number(product?.store_qty) || 0)
  );
  const [minStock, setMinStock] = useState(product?.min_stock ?? 0);
  const [submitting, setSubmitting] = useState(false);

  const [existingProducts, setExistingProducts] = useState(products || []);
  useEffect(() => {
    if (!products || products.length === 0) {
      api.get("/inventory/skus")
        .then(res => {
          setExistingProducts(res.data || []);
        })
        .catch(err => console.error(err));
    }
  }, [products]);

  const uniqueBrands = Array.from(new Set(existingProducts.map(p => p.brand).filter(Boolean)));
  const uniqueVendors = Array.from(new Set(existingProducts.map(p => p.vendor_name || p.vendor).filter(Boolean)));

  if (!product) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter a product name");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.put(`/inventory/product/${product.id}`, {
        name: name.trim(),
        category,
        ledger,
        brand: brand.trim(),
        vendor_name: vendorName.trim(),
        unit,
        stock_unit: stockUnit,
        qty: parseFloat(qty) || 0,
        unit_price: parseFloat(unitPrice) || 0,
        store_qty: parseFloat(storeQty) || 0,
        min_stock: isNaN(parseFloat(minStock)) ? 0 : parseFloat(minStock),
      });

      toast.success(`Successfully updated item '${res.data.name}'!`);
      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-in fade-in duration-200" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white border border-slate-200 shadow-2xl rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col text-slate-950 overflow-hidden"
       onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 p-6 text-white flex items-center justify-between border-b border-amber-500/30 shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <span className="lss-overline text-amber-300">Inventory Master Registry</span>
            </div>
            <h3 className="font-serif-lux text-2xl font-bold tracking-tight text-white mt-0.5">
              Edit Inventory Product Item
            </h3>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">
                Product Name <span className="text-rose-600">*</span>
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="lss-input w-full text-xs font-bold"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Category</label>
              <SearchableSelect
                options={CATEGORIES}
                value={category}
                onChange={(val) => setCategory(val)}
                placeholder="Select Category"
                getOptionLabel={(c) => c}
                getOptionValue={(c) => c}
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Qty Unit (pack size)</label>
              <SearchableSelect
                options={QTY_UNITS}
                value={unit}
                onChange={(val) => setUnit(val)}
                placeholder="Select Unit"
                getOptionLabel={(u) => u}
                getOptionValue={(u) => u}
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Stock Unit (bottle / piece)</label>
              <SearchableSelect
                options={STOCK_UNITS}
                value={stockUnit}
                onChange={(val) => setStockUnit(val)}
                placeholder="Select Unit"
                getOptionLabel={(u) => u}
                getOptionValue={(u) => u}
              />
            </div>

            <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Ledger (Retail sells via POS · Technical consumes via services)</label>
                <SearchableSelect
                  options={["retail", "technical"]}
                  value={ledger}
                  onChange={(val) => setLedger(val)}
                  placeholder="Select Ledger"
                  getOptionLabel={(l) => (l === "technical" ? "Technical (service consumption)" : "Retail (POS sale)")}
                  getOptionValue={(l) => l}
                />
              </div>

            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Brand</label>
              <input
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="lss-input w-full text-xs font-semibold"
                list="edit-product-brands"
              />
              <datalist id="edit-product-brands">
                {uniqueBrands.map(b => (
                  <option key={b} value={b} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Vendor Name</label>
              <input
                type="text"
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                className="lss-input w-full text-xs font-semibold"
                list="edit-product-vendors"
              />
              <datalist id="edit-product-vendors">
                {uniqueVendors.map(v => (
                  <option key={v} value={v} />
                ))}
              </datalist>
            </div>
          </div>

          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-4">
            <span className="lss-overline text-amber-900">Pricing & Stock Adjustments</span>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <label className="font-bold text-slate-700 block mb-1">Unit Price (₹) — drives valuation</label>
                <input
                  type="number"
                  step="any"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                  className="lss-input w-full font-bold tabular"
                />
              </div>
              <div>
                <label className="font-bold text-slate-700 block mb-1">Qty per Unit (size)</label>
                <input
                  type="number"
                  step="any"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  className="lss-input w-full font-bold tabular"
                />
              </div>
              <div>
                <label className="font-bold text-slate-700 block mb-1">
                  {category === "Retail" ? "Opening Stock (1 Sep 2026)" : "Opening Stock (21 Aug 2026)"}
                </label>
                <input
                  type="number"
                  step="any"
                  value={storeQty}
                  onChange={(e) => setStoreQty(e.target.value)}
                  className="lss-input w-full font-bold tabular"
                />
                <span className="text-[10px] text-slate-500 block mt-0.5 font-medium">
                  {category === "Retail" ? "Changes 1 Sep 2026 retail opening" : "Changes 21 Aug 2026 opening"}
                </span>
              </div>
              <div>
                <label className="font-bold text-slate-700 block mb-1">Min Stock Alert</label>
                <input
                  type="number"
                  value={minStock}
                  onChange={(e) => setMinStock(e.target.value)}
                  className="lss-input w-full font-bold tabular"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="lss-btn-outline px-4 py-2.5 text-xs font-bold uppercase tracking-wider"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="lss-btn-gold px-6 py-2.5 text-xs font-extrabold uppercase tracking-wider shadow-md disabled:opacity-50 flex items-center gap-1.5"
            >
              <Edit3 className="w-4 h-4" />
              <span>{submitting ? "Updating..." : "Save Product Changes"}</span>
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
